import Column from "@/Components/Grid/Column";
import Row from "@/Components/Grid/Row";
import HeadingWithDecor from "@/Components/HeadingWithDecor";
import PatientDocCard from "@/Components/Patient/PatientDocCard";
import PatientMovieBlock from "@/Components/Patient/PatientMovieBlock";
import PatientPodcastBlock from "@/Components/Patient/PatientPodcastBlock";
import PatientPostItem from "@/Components/Patient/PatientPostItem";
import DocCard from "@/Components/Platform/Docs/DocCard";
import MovieBlock from "@/Components/Platform/MovieBlock";
import PodcastBlock from "@/Components/Platform/PodcastBlock";
import PostItem from "@/Components/Platform/Posts/PostItem";
import TextInput from "@/Components/TextInput";
import { SelectInputProps } from "@/types/components/SelectInputProps";
import { InertiaPage } from "@/types/inertia";
import { ArticleModel } from "@/types/models/ArticleModel";
import { CategorySeriesModel } from "@/types/models/CategorySeriesModel";
import { DocumentModel } from "@/types/models/DocumentModel";
import { PodcastSeriesModel } from "@/types/models/PodcastSeriesModel";
import { TherapeuticAreaModel } from "@/types/models/TherapeuticAreaModel";
import { VideoModel } from "@/types/models/VideoModel";
import { Transition } from "@headlessui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { usePage } from "@inertiajs/react";
import axios from "axios";
import { Fragment, useEffect, useState } from "react";
import ReactSelect from "react-select";

const PatientSearchMenu = ({ state, setState }: {
    state: boolean,
    setState: (value: boolean) => void
}) => {
    const formatDate = (dateString: string) => {
        return dateString.split(' ')[0];
    };

    const [showFilters, setShowFilters] = useState(true);
    const { areas } = usePage<InertiaPage>().props;
    const [therapeuticAreasOptions, setTherapeuticAreasOptions] = useState<SelectInputProps[]>([]);

    const [filteredData, setFilteredData] = useState<{
        videos: VideoModel[],
        podcasts: PodcastSeriesModel[],
        series: CategorySeriesModel[],
        articles: ArticleModel[],
        docs: DocumentModel[],
    }>({
        videos: [],
        podcasts: [],
        series: [],
        articles: [],
        docs: [],
    })

    const [searchData, setSearchData] = useState({
        q: '',
        area: '',
        model: ''
    })

    useEffect(() => {
        if (areas) {
            const mappedOptions: SelectInputProps[] = areas.map((item: TherapeuticAreaModel, i: number) => ({
                value: item.id,
                label: item.name,
            }))

            setTherapeuticAreasOptions(mappedOptions);
        }

        setSearchData((prev) => ({
            ...prev,
            'model': 'all'
        }));
    }, [areas])

    const selectOptionsKind: SelectInputProps[] = [
        { value: 'videos', label: 'Video' },
        { value: 'podcasts', label: 'Podcast' },
        { value: 'articles', label: 'Artykuł' },
        { value: 'docs', label: 'Dokument' },
        { value: 'all', label: 'Wszystko' },
    ]

    const handleInputChange = (name: string, value: string | undefined) => {
        setSearchData((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    useEffect(() => {
        const hasNonEmptyQOrArea = searchData.q !== '' || searchData.area !== '';

        const fetchResults = async () => {
            const results = await axios.get(route('patient.searchResults', {
                ...searchData
            }));

            setFilteredData(results.data);

        };

        if (hasNonEmptyQOrArea && searchData.q.length >= 5) {
            fetchResults();
        }
    }, [searchData]);

    const checkIfAnyKeyExists = () => {
        return Object.values(filteredData).some(item => item.length > 0);
    }

    useEffect(() => {
        if (state === true) {
            setFilteredData({
                videos: [],
                podcasts: [],
                series: [],
                articles: [],
                docs: [],
            })

            setSearchData({
                q: '',
                area: '',
                model: 'all'
            })
        }
    }, [state])

    return (
        <>
            <Transition
                as={Fragment}
                show={state}
                enter="transition-theme"
                enterFrom="opacity-0 translate-x-[50%]"
                enterTo="opacity-100 translate-x-[0%]"
                leave="transition-theme"
                leaveFrom="opacity-100 translate-x-[0%]"
                leaveTo="opacity-0 translate-x-[50%]"
            >
                <div className="bg-gradientMain px-14 pb-8 absolute right-0 top-0 z-10 w-full h-screen overflow-y-scroll maxMd:px-4 maxMd:pb-20">
                    <div className="pt-22 flex flex-col h-full">
                        <div className="mt-4 mb-14 maxMd:mt-6 maxMd:mb-8">
                            <form>
                                <Row className="gap-y-4">
                                    <Column xxl={7} xl={5} lg={5}>
                                        <TextInput
                                            id="q"
                                            type="text"
                                            name="q"
                                            onChange={(e) => handleInputChange('q', e.target.value)}
                                            className="w-full"
                                            isFocused={true}
                                            placeholder='Napisz nam czego szukasz (min. 5 znaków)'
                                            icon='ph:magnifying-glass-bold'
                                        />
                                    </Column>
                                    {showFilters &&

                                        <>
                                            <Column xxl={3} xl={4} lg={4} className="mb-4">
                                                <div className={`react-select-input-container relative z-[20]`}>
                                                    <label htmlFor="react-select-input" className="text-12 leading-16 text-accordorange tracking-[0.18px] absolute top-2 left-4 z-40">
                                                        Kategoria
                                                    </label>
                                                    <ReactSelect
                                                        className={`react-select-input`}
                                                        onChange={(e) => handleInputChange('area', e?.value)}
                                                        options={therapeuticAreasOptions}
                                                        unstyled
                                                        id="react-select-input"
                                                        placeholder={'Wpisz lub wybierz z listy'}
                                                        noOptionsMessage={() => 'Nie znaleziono opcji'}
                                                        isClearable={false}
                                                        autoFocus={false}
                                                        classNames={{
                                                            control: (state) => {
                                                                const borderColor = !state.isFocused ? 'border-royalPeriwinkle' : 'border-accordorange'

                                                                return `transition-theme bg-deepIndigo pt-[26px] pb-2 pl-4 pr-12 border-1 rounded ${borderColor} outline-none ring-0 ring-offset-0`
                                                            },
                                                            indicatorSeparator: (state) => {
                                                                return 'hidden'
                                                            },

                                                            input: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px] border-0'
                                                            },
                                                            placeholder: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px]'
                                                            },
                                                            valueContainer: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px]'
                                                            },
                                                            menu: (state) => {
                                                                return 'bg-midnightNavy mt-2 shadow-selectShadow border-royalPeriwinkle rounded-[4px]'
                                                            },
                                                            dropdownIndicator: (state) => {
                                                                const showClass = 'cursor-pointer text-royalPeriwinkle w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]'
                                                                return showClass
                                                            },
                                                            clearIndicator: (state) => {
                                                                return 'text-accordorange cursor-pointer w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]'
                                                            },
                                                            noOptionsMessage: (state) => {
                                                                return 'bg-midnightNavy mt-2 px-4 pt-1 pb-3 shadow-selectShadow border-royalPeriwinkle rounded-[4px] text-arcticMist text-16 leading-6'
                                                            },
                                                            option: (state) => {
                                                                const bgColor = state.isFocused ? 'bg-accordorange text-midnightNavy' : ''
                                                                return ` pl-4 py-3 pr-10 text-arcticMist text-16 leading-6 ${bgColor}`
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Column>
                                            <Column xxl={2} xl={3} lg={3}>
                                                <div className={`react-select-input-container relative z-[10]`}>
                                                    <label htmlFor="react-select-input" className="text-12 leading-16 text-accordorange tracking-[0.18px] absolute top-2 left-4 z-30">
                                                        Typ materiału
                                                    </label>
                                                    <ReactSelect
                                                        className={`react-select-input`}
                                                        onChange={(e) => handleInputChange('model', e?.value)}
                                                        options={selectOptionsKind}
                                                        unstyled
                                                        defaultValue={{ value: '4', label: 'Wszystko' }}
                                                        id="react-select-input"
                                                        placeholder={'Wpisz lub wybierz z listy'}
                                                        noOptionsMessage={() => 'Nie znaleziono opcji'}
                                                        isClearable={false}
                                                        autoFocus={false}
                                                        classNames={{
                                                            control: (state) => {
                                                                const borderColor = !state.isFocused ? 'border-royalPeriwinkle' : 'border-accordorange'

                                                                return `transition-theme bg-deepIndigo pt-[26px] pb-2 pl-4 pr-12 border-1 rounded ${borderColor} outline-none ring-0 ring-offset-0`
                                                            },
                                                            indicatorSeparator: (state) => {
                                                                return 'hidden'
                                                            },

                                                            input: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px] border-0'
                                                            },
                                                            placeholder: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px]'
                                                            },
                                                            valueContainer: (state) => {
                                                                return 'text-arcticMist text-16 leading-6 tracking-[0.24px]'
                                                            },
                                                            menu: (state) => {
                                                                return 'bg-midnightNavy mt-2 shadow-selectShadow border-royalPeriwinkle rounded-[4px]'
                                                            },
                                                            dropdownIndicator: (state) => {
                                                                const showClass = 'cursor-pointer text-royalPeriwinkle w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]'
                                                                return showClass
                                                            },
                                                            clearIndicator: (state) => {
                                                                return 'text-accordorange cursor-pointer w-[24px] h-[24px] absolute top-1/2 -translate-y-1/2 right-4 [&>svg]:w-[24px] [&>svg]:h-[24px]'
                                                            },
                                                            noOptionsMessage: (state) => {
                                                                return 'bg-midnightNavy mt-2 px-4 pt-1 pb-3 shadow-selectShadow border-royalPeriwinkle rounded-[4px] text-arcticMist text-16 leading-6'
                                                            },
                                                            option: (state) => {
                                                                const bgColor = state.isFocused ? 'bg-accordorange text-midnightNavy' : ''
                                                                return ` pl-4 py-3 pr-10 text-arcticMist text-16 leading-6 ${bgColor}`
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Column>
                                        </>
                                    }
                                    <Column className="maxMd:block hidden">
                                        <button type="button" className="outline-btn flex gap-x-2 mx-auto" onClick={() => setShowFilters((prev) => !prev)}>
                                            <Icon icon={`tabler:chevron-up`} className={`text-arcticMist ${showFilters ? 'rotate-180' : ''}`} width={24} />
                                            {showFilters ? 'Ukryj filtry' : 'Pokaż filtry'}
                                        </button>
                                    </Column>
                                </Row>
                            </form>
                        </div>
                        {filteredData.videos && filteredData.videos.length > 0 &&
                            <section className="mb-20 maxMd:mb-10">
                                <Row>
                                    <Column>
                                        <HeadingWithDecor as="p">
                                            Filmy
                                        </HeadingWithDecor>
                                    </Column>
                                </Row>
                                <Row className="gap-y-6">
                                    {filteredData.videos.map((d, i) => (
                                        <Column xl={3} lg={3} md={4} key={i}>
                                            <PatientMovieBlock
                                                image={'patient' + d.hero + '/' + d.id + '/video'}
                                                timeSummary={d.time}
                                                title={d.name}
                                                link={route('front.patient.movies.single.player', { video: d })}
                                                model={d}
                                                is_pin_protected={Boolean((d as any).is_pin_protected)}
                                            />
                                        </Column>
                                    ))}
                                </Row>
                            </section>
                        }
                        {filteredData.podcasts && filteredData.podcasts.length > 0 &&
                            <section className="mb-20 maxMd:mb-10">
                                <Row>
                                    <Column>
                                        <HeadingWithDecor as="p">
                                            Podcasty
                                        </HeadingWithDecor>
                                    </Column>
                                </Row>
                                <Row className="gap-y-6">
                                    {filteredData.podcasts.map((d, i) => (
                                        <Column xl={2} lg={2} md={3} key={i}>
                                            <PatientPodcastBlock
                                                image={'patient' + d.podcasts[0].hero + '/' + d.id + '/podcastSeries'}
                                                createdAt={formatDate(d.created_at)}
                                                title={d.name}
                                                link={route('front.patient.podcasts.single', { podcastSeries: d, podcast: d.podcasts[0]?.slug })}
                                                is_pin_protected={Boolean((d as any).is_pin_protected)}
                                            />
                                        </Column>
                                    ))}
                                </Row>
                            </section>
                        }
                        {filteredData.articles && filteredData.articles.length > 0 &&
                            <section className="mb-20">
                                <Row>
                                    <Column>
                                        <HeadingWithDecor as="p">
                                            Artykuły
                                        </HeadingWithDecor>
                                    </Column>
                                </Row>
                                <Row className="gap-y-6 maxMd:gap-y-0">
                                    {filteredData.articles.map((d, i) => (
                                        <Column xl={6} lg={6} md={6} key={i} className="maxMd:mb-10">
                                            <PatientPostItem key={i} {...d} />
                                        </Column>
                                    ))}
                                </Row>
                            </section>
                        }
                        {filteredData.docs && filteredData.docs.length > 0 &&
                            <section className="mb-20">
                                <Row>
                                    <Column>
                                        <HeadingWithDecor as="p">
                                            Dokumenty
                                        </HeadingWithDecor>
                                    </Column>
                                </Row>
                                <Row className="gap-y-6">
                                    {filteredData.docs.map((d, i) => (
                                        <Column xl={3} md={6} lg={4} key={i}>
                                            <PatientDocCard
                                                file={d.file}
                                                title={d.name}
                                                id={d.id}
                                                slug={d.slug}
                                                is_pin_protected={Boolean((d as any).is_pin_protected)}
                                            />
                                        </Column>
                                    ))}
                                </Row>
                            </section>
                        }
                        <Transition
                            as={Fragment}
                            show={searchData.q.length >= 5 && !checkIfAnyKeyExists()}
                            enter="transition ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Row className="justify-center my-auto">
                                <Column xl={6} className="text-center text-arcticMist">
                                    <p className="h5 font-semibold mb-2">Ups! nie udało nam się znaleźć wyników dla wyszukiwanej frazy.</p>
                                    <p className="text-body mb-6">Zachęcamy do sprawdzenia poprawności wprowadzonych fraz lub wprowadzenia innych kryteriów wyszukiwania.</p>
                                    <img src="/storage/static/no-search.svg" alt="Brak wyników" className="mx-auto" />
                                    <p className="text-body mt-6">Polecamy również zapoznanie się z naszymi nowościami</p>
                                </Column>
                            </Row>
                        </Transition>
                    </div>
                </div>
            </Transition>
        </>
    )
}

export default PatientSearchMenu;